import React from 'react';
import { buildCloudflareImageSrcSet, buildCloudflareImageUrl } from '@skin-seoul/utils';
import { useProductCard } from '../ProductCardContext';

/**
 * Candidate widths for the card image. The image box is 200px tall on mobile and 300px on
 * tablet+ (see VerticalProductCardImage) and cards are ~160–332px wide, so 320–800 covers
 * 1x–3x device pixel ratios without ever requesting the 2000px original.
 */
export const PRODUCT_CARD_IMAGE_WIDTHS = [320, 480, 640, 800] as const;

/** Mobile cards sit two-up (~45vw); tablet+ cards are capped at 332px. */
export const PRODUCT_CARD_IMAGE_SIZES = '(max-width: 767px) 45vw, 332px';

const ProductImage = () => {
  const { productImage, name, imagePriority } = useProductCard();

  // shared-components cannot use next/image, so resize through Cloudflare directly.
  // Non-transformable sources (relative paths, third-party hosts) fall back to a plain <img>.
  const srcSet = buildCloudflareImageSrcSet(productImage, PRODUCT_CARD_IMAGE_WIDTHS);
  const src = srcSet ? buildCloudflareImageUrl(productImage, { width: PRODUCT_CARD_IMAGE_WIDTHS[1] }) : productImage;

  return (
    <div className="flex items-center h-full justify-center w-full min-w-0 overflow-hidden">
      <img
        alt={name}
        src={src}
        srcSet={srcSet || undefined}
        sizes={srcSet ? PRODUCT_CARD_IMAGE_SIZES : undefined}
        // Intrinsic size only informs the aspect ratio; the container already fixes the box.
        width={300}
        height={300}
        className="w-full h-full object-contain max-w-full max-h-full"
        style={{ mixBlendMode: 'multiply' }}
        // First-viewport cards are the listing's LCP candidate: a lazy image there waits for layout
        // before it is even requested (Lighthouse "lcp-lazy-loaded"). Everything below stays lazy.
        loading={imagePriority ? 'eager' : 'lazy'}
        fetchPriority={imagePriority ? 'high' : undefined}
        decoding="async"
      />
    </div>
  );
};

export default ProductImage;
